import React, { useMemo, useState, useEffect, Fragment, useContext } from 'react'
import { Redirect } from 'react-router'
import NavBar from 'components/NavBar'
import ItemActions from '../ItemActions'
import get from 'lodash/get'
import PageNotFound from 'components/PageNotFound'
import useItemData from '../hooks/useItemData'
import useMultiUpdate from '../hooks/useMultiUpdate'
import merge from 'lodash/merge'

import { StyledColItem } from '../styled'
import globalStyles from 'style/app.module.scss'
import { RefreshSideListContext } from '../context/RefreshSideListContext'

export const DataModVerContext = React.createContext()
export const OpenTabContext = React.createContext()

const ItemView = ({ xs, itemActionsProps: itemActionsAdditionalProps = {}, baseUrl, subsectionProps = {}, ...props }) => {
	const { sectionData: { multiEditConfig, itemViewProps } } = props
	const { appData, navBarProps: { getLabel, ...navBarProps } = {}, multiEdit } = props
	const { handleMutation, mutations, addNewRecord: { status: newRecordStatus } = {}, listQueryRef } = props
	const { sectionData = {}, section, subsection, subsectionData, recordId: id } = props
	const { component: SubsectionComponent } = subsectionData
	const { status, isLoading, itemLabel, itemData, formData, labelInfo, queryResult } = useItemData(props)
	const newRecord = !!newRecordStatus && newRecordStatus !== 'RECORD_ADDED'
	const { refreshItemData, setRefreshItemData } = useContext(RefreshSideListContext)

	useEffect(() => {
		if (refreshItemData && queryResult.refetch) {
			queryResult.refetch().then(() => setRefreshItemData(false))
		}
	}, [refreshItemData])

	const { ConfirmButton, ConfirmModal, onChangeMultiEdit, apiRef } = useMultiUpdate({ ...props, formData, newRecord, multiEdit, multiEditConfig })

	const [openTabState, setOpenTabState] = useState({ recordId: id })

	const handleOpenTab = (isExpanded, blockId, label) => {
		setOpenTabState({ ...openTabState, id: isExpanded ? blockId : null, label: isExpanded ? label : null })
	}

	useEffect(() => {
		setOpenTabState({ ...openTabState, recordId: id })
	}, [id])

	let ItemComponent = null
	const itemActions = useMemo(() => sectionData?.itemActions || [], [sectionData.itemActions])
	const itemActionsProps = useMemo(() => merge({}, {
		item: itemData,
		actions: sectionData.itemActions
	}, itemActionsAdditionalProps), [itemData, sectionData?.itemActions, itemActionsAdditionalProps])
	const isFilterSubsections = useMemo(() => sectionData.subsections.some(item => typeof item.isHidden === 'function'), [section])
	const subsections = useMemo(() => {
		if (isFilterSubsections) {
			return sectionData.subsections.filter(item => !(typeof item.isHidden === 'function' && item.isHidden(itemData, props)))
		}
		return sectionData.subsections
	}, [isFilterSubsections, itemData])
	const xsVal = typeof xs === 'number' ? xs : true
	const isSubsectionRedirect = !subsections.some?.(item => item?.link === subsection)

	if (isSubsectionRedirect) {
		const visibleSubsection = subsections[0]?.link
		return <Redirect to={`${baseUrl ? baseUrl : ''}/${section}${visibleSubsection ? `/${visibleSubsection}` : ''}/${id || ''}`} />
	}

	switch (status) {
		case 'NETWORK_ERROR':
			ItemComponent = <PageNotFound type='NETWORK_ERROR' />
			break
		case 'NO_ITEM':
			ItemComponent = <PageNotFound type='NO_ITEM' />
			break
		case 'ITEM':
			ItemComponent = (
				<Fragment>
					<NavBar
						// id='sectionPage'
						className='section'
						label={itemLabel}
						labelInfo={(isLoading || refreshItemData) ? null : labelInfo}
						navContent={subsections}
						isContentLoading={isLoading || refreshItemData}
						getLink={l => `${baseUrl ? '/' + baseUrl : ''}/${section}${l ? '/' + l : ''}${id ? '/' + id : ''}`}
						{...itemActions.length && { headerComponents: <ItemActions {...itemActionsProps} handlerProps={props} /> }}
						subNav={true}
						{...navBarProps}
					>
						<DataModVerContext.Provider value={get(itemData, 'mod_ver')}>
							<OpenTabContext.Provider value ={{ openTabState, handleOpenTab }}>
								<SubsectionComponent
									key={id + isLoading}
									appData={appData}
									itemId={newRecordStatus ? 'neue' : get(itemData, 'id') || '-1'}
									data={itemData}
									formData={formData}
									listQueryRef={listQueryRef}
									itemViewProps={itemViewProps}
									mutations={{
										...mutations,
										...mutations.updateItem && { updateItem: mutations.updateItem(id, { subsectionData, sectionData, formData }) }
									}}
									ConfirmButton={ConfirmButton}
									ConfirmModal={ConfirmModal}
									onChangeMultiEdit={onChangeMultiEdit}
									apiRef={apiRef}
									handleMutation={handleMutation}
									newRecord={newRecord}
									multiEdit={multiEdit}
									queryResult={queryResult}
									{...subsectionProps}
								/>
							</OpenTabContext.Provider>
						</DataModVerContext.Provider>
					</NavBar>
				</Fragment>
			)
			break
		default:
			ItemComponent = null
			break
	}

	return (
		<StyledColItem xs={xsVal} className={globalStyles.itemView}>
			{ItemComponent}
		</StyledColItem>
	)
}

export default ItemView
